import { useState } from 'react';
import { toast } from 'sonner';

import { mensajeDe } from '@/api/errors';
import { Campo } from '@/components/Campo';
import { Modal } from '@/components/Modal';
import { SubirVarios, type ArchivoSubidoCola } from '@/components/SubirVarios';

import { ElegirLicencias } from '../ElegirLicencias';
import { SelectorEspecialidad } from '../comun/SelectorEspecialidad';
import { useResumenBiblioteca } from '../queries';
import { SIMPLES } from '../tipos';
import { useAltaMasiva } from './queries';

/**
 * Alta de varias flashcards o guías GPC a la vez: un elemento de la biblioteca por archivo, todos
 * con la misma especialidad, etiquetas, banderas y licencias (como el legacy).
 */
export function AltaMasiva({
  tipo,
  onCerrar,
}: {
  tipo: 'flashcard' | 'guia-gpc' | null;
  onCerrar: () => void;
}) {
  return (
    <Modal
      abierto={tipo !== null}
      onCerrar={onCerrar}
      titulo={tipo === 'guia-gpc' ? 'Nuevas guías GPC' : 'Nuevas flashcards'}
      descripcion="Sube los archivos; cada uno se crea como un elemento de la biblioteca."
      ancho="max-w-4xl"
    >
      {tipo && <Formulario key={tipo} tipo={tipo} onListo={onCerrar} />}
    </Modal>
  );
}

function Formulario({ tipo, onListo }: { tipo: 'flashcard' | 'guia-gpc'; onListo: () => void }) {
  const t = SIMPLES[tipo];
  const flash = tipo === 'flashcard';
  const alta = useAltaMasiva(flash ? 'flashcards' : 'gpc-guides');
  const resumen = useResumenBiblioteca();
  const [especialidad, setEspecialidad] = useState<number | null>(null);
  const [etiquetas, setEtiquetas] = useState('');
  const [banderas, setBanderas] = useState({
    isEnglish: false,
    isPlus: false,
    isEvaluatedEnarm: false,
  });
  const [licencias, setLicencias] = useState<Set<number>>(new Set());
  const [listos, setListos] = useState<ArchivoSubidoCola[]>([]);
  const [pendientes, setPendientes] = useState(0);
  if (!t?.archivo) return null;

  const crear = () => {
    if (flash && especialidad === null) {
      toast.error('Elige la especialidad.');
      return;
    }
    if (listos.some((a) => a.nombre.length < 2)) {
      toast.error('Cada archivo necesita un nombre (mínimo 2 caracteres).');
      return;
    }
    alta.mutate(
      {
        specialtyId: especialidad,
        tags: etiquetas
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        isEnglish: banderas.isEnglish,
        isPlus: banderas.isPlus,
        ...(flash && { isEvaluatedEnarm: banderas.isEvaluatedEnarm }),
        membershipIds: [...licencias],
        files: listos.map((a) => ({ name: a.nombre, filePath: a.path })),
      },
      {
        onSuccess: (r) => {
          toast.success(`${r.length} elemento(s) creado(s).`);
          onListo();
        },
        onError: (e) => toast.error(mensajeDe(e)),
      },
    );
  };

  const casilla = (k: keyof typeof banderas, etiqueta: string) => (
    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        className="h-4 w-4 accent-[var(--color-azul-600)]"
        checked={banderas[k]}
        onChange={(e) => setBanderas((b) => ({ ...b, [k]: e.target.checked }))}
      />
      {etiqueta}
    </label>
  );

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1.3fr]">
      <div className="space-y-4">
        <Campo etiqueta={flash ? 'Especialidad *' : 'Especialidad'}>
          <SelectorEspecialidad
            valor={especialidad}
            requerido={flash}
            vacio={flash ? 'Elige una especialidad' : 'Sin especialidad'}
            onCambio={setEspecialidad}
          />
        </Campo>
        <Campo etiqueta="Etiquetas" ayuda="Separadas por coma; se ponen a todos.">
          <input
            className="input-vidrio"
            value={etiquetas}
            onChange={(e) => setEtiquetas(e.target.value)}
          />
        </Campo>
        <div className="flex flex-wrap gap-4">
          {casilla('isEnglish', 'En inglés')}
          {casilla('isPlus', 'Plus')}
          {flash && casilla('isEvaluatedEnarm', 'Evaluada en el ENARM')}
        </div>
        {resumen.data && (
          <section>
            <h3 className="mb-2 text-sm font-extrabold">Licencias (opcional)</h3>
            <ElegirLicencias
              licencias={resumen.data.memberships}
              elegidas={licencias}
              onCambio={(id, si) =>
                setLicencias((s) => {
                  const n = new Set(s);
                  if (si) n.add(id);
                  else n.delete(id);
                  return n;
                })
              }
            />
          </section>
        )}
      </div>
      <div className="space-y-4">
        <SubirVarios
          destino={t.archivo.destino}
          tipos={t.archivo.tipos}
          maxNombre={t.nombreMax}
          onCambio={(l, p) => {
            setListos(l);
            setPendientes(p);
          }}
        />
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-cs btn-contorno" onClick={onListo}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-cs btn-primario"
            disabled={listos.length === 0 || pendientes > 0 || alta.isPending}
            title={
              pendientes > 0
                ? 'Espera a que terminen de subir (o quita los que fallaron)'
                : undefined
            }
            onClick={crear}
          >
            {alta.isPending ? 'Creando…' : `Crear ${listos.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}
